// "Where today's trades went" on Home > Overview: today's board trades (org.eggs.get_trades
// 'current') as flows from the seller's region to the buyer's region, split by product (EggType).
//
// - Every get_trades row is one trade, as on the board and in Market today: the parts of a combo
//   each count. Volume is the number of trades, not Qty: Qty is in the pack's units (cases, pounds
//   of liquid), and turning it into cases needs a dozens-per-pack table the app doesn't have.
// - Formula-priced trades count like any other: a trade's regions don't depend on its price.
// - The server pads its fixed-width columns ("PGL ", "NE "): regions and types are trimmed.
//
// The chart (directives/region-flow-card.js, from charts/regionFlowView.js) draws what layoutFlows
// works out here: seller regions down the left, buyer regions down the right, both in one
// geographic order, a ribbon per lane and product, grouped by product in every bar. Pure maths, no Angular: tested in
// regionFlows.test.mjs.
//
// A port of the ECI mobile app's src/services/regionFlows.ts. Keep the two in step.

import moment from 'moment';

/**
 * The fields of a get_trades row this reads (the previous report's rows carry them too).
 * @typedef {object} FlowTrade
 * @property {?string} [SellerRegion]
 * @property {?string} [BuyerRegion]
 * @property {?string} [EggType]
 */

/**
 * The regions in one fixed order, north to south and west to east, on both sides of the chart: a
 * lane within a region runs straight across, and the picture keeps its shape from day to day.
 * Regions not listed (or new) follow, alphabetically; a missing one ('?') comes last.
 */
export const REGION_ORDER = ['NW', 'CN', 'NE', 'MW', 'SW', 'SC', 'SE'];

/** What a screen reader says for a region (the chart shows the code). */
export const REGION_NAMES = {
  NW: 'Northwest', CN: 'Canada', NE: 'Northeast', MW: 'Midwest', SW: 'Southwest', SC: 'South Central',
  SE: 'Southeast',
};

/**
 * The products with a colour of their own, busiest first (30 weekdays to 2026-09-29: PGL 503
 * trades, LIQ 162, EGL 140, CTN 42, GNR 26, TGL 23; nothing else). A lane's bands stack in this
 * order and the legend lists them in it. Any other type (FRZN, NRBS, ...) is drawn as 'Other'.
 */
export const PRODUCTS = ['PGL', 'LIQ', 'EGL', 'CTN', 'GNR', 'TGL'];
export const OTHER = 'Other';
/**
 * A product colour: one of PRODUCTS, or OTHER. On the web each slot has a custom property in
 * main.css, --hc-p-<slot>, and the class hc-p-<slot> fills with it.
 * @typedef {'PGL' | 'LIQ' | 'EGL' | 'CTN' | 'GNR' | 'TGL' | 'Other'} ProductSlot
 */
/** @type {ProductSlot[]} */
export const PRODUCT_SLOTS = [...PRODUCTS, OTHER];

/** A region code as the board shows it: trimmed, upper case; '?' when there is none. */
export const regionOf = region => (region ?? '').trim().toUpperCase() || '?';

/** A trade's product: its EggType, trimmed; '?' when there is none. */
export const productOf = t => (t.EggType ?? '').trim().toUpperCase() || '?';

/** What a product is called aloud and in the legend: its code, or "unknown type" for '?'. */
export const productName = product => (product === '?' ? 'unknown type' : product);

/** The colour a product is drawn in: its own, or Other's. */
export const productSlot = product => (PRODUCTS.includes(product) ? product : OTHER);

const slotIndex = slot => PRODUCT_SLOTS.indexOf(slot);

/** Regions in REGION_ORDER, then the rest alphabetically, then '?'. */
export function compareRegions(a, b) {
  const rank = r => {
    const i = REGION_ORDER.indexOf(r);
    return i >= 0 ? i : r === '?' ? REGION_ORDER.length + 1 : REGION_ORDER.length;
  };
  return rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0);
}

/**
 * One product's trades in a lane, or in the day.
 * @typedef {object} ProductCount
 * @property {string} product
 * @property {number} count
 */

/**
 * One colour's trades: a band. Other's names the types it stands for.
 * @typedef {object} BandCount
 * @property {ProductSlot} slot
 * @property {number} count
 * @property {ProductCount[]} types The types drawn in this colour, busiest first.
 */

/**
 * Trades from one seller region to one buyer region.
 * @typedef {object} Lane
 * @property {string} from
 * @property {string} to
 * @property {number} count
 * @property {BandCount[]} bands By colour, in PRODUCT_SLOTS order: the ribbon's bands, top to bottom.
 * @property {ProductCount[]} types By type, busiest first (then in PRODUCT_SLOTS order, then by
 *   name): what's read out.
 */

/**
 * @typedef {object} Flows
 * @property {number} total
 * @property {string[]} regions Every region with a trade on either side, in chart order.
 * @property {Object<string, number>} sold Trades sold from each region (the left column)...
 * @property {Object<string, number>} bought ...and bought into it (the right).
 * @property {Lane[]} lanes Busiest first; ties in chart order (seller, then buyer).
 * @property {BandCount[]} products The day's colours, in PRODUCT_SLOTS order: the legend.
 */

const compareTypes = (a, b) => b.count - a.count
  || slotIndex(productSlot(a.product)) - slotIndex(productSlot(b.product))
  || (a.product < b.product ? -1 : a.product > b.product ? 1 : 0);

/** Counts per type, as bands (one per colour, in slot order) and as a list (busiest first). */
function tally(trades) {
  const byType = new Map();
  trades.forEach(t => byType.set(productOf(t), (byType.get(productOf(t)) || 0) + 1));
  const types = [...byType.entries()].map(([product, count]) => ({product, count})).sort(compareTypes);
  const bands = PRODUCT_SLOTS
    .map(slot => {
      const of = types.filter(t => productSlot(t.product) === slot);
      return {slot, count: of.reduce((n, t) => n + t.count, 0), types: of};
    })
    .filter(b => b.count > 0);
  return {bands, types};
}

/**
 * The day's trades as lanes between regions, each split by product.
 * @param {FlowTrade[]} trades
 * @returns {Flows}
 */
export function regionFlows(trades) {
  const lanes = new Map();
  const sold = {};
  const bought = {};
  trades.forEach(t => {
    const from = regionOf(t.SellerRegion);
    const to = regionOf(t.BuyerRegion);
    const key = `${from}>${to}`;
    const lane = lanes.get(key) || {from, to, trades: []};
    lane.trades.push(t);
    lanes.set(key, lane);
    sold[from] = (sold[from] || 0) + 1;
    bought[to] = (bought[to] || 0) + 1;
  });
  const regions = [...new Set([...Object.keys(sold), ...Object.keys(bought)])].sort(compareRegions);
  return {
    total: trades.length,
    regions,
    sold,
    bought,
    lanes: [...lanes.values()]
      .map(({from, to, trades: laneTrades}) => ({from, to, count: laneTrades.length, ...tally(laneTrades)}))
      .sort((a, b) => b.count - a.count || compareRegions(a.from, b.from) || compareRegions(a.to, b.to)),
    products: tally(trades).bands,
  };
}

/** A band's egg types that the board can filter by: all but '?' (no type). */
const typesOf = band => (band ? band.types.map(t => t.product).filter(product => product !== '?') : []);

/**
 * The Trades board's filter for one of the chart's colours: its egg types, as the board's Egg Type
 * filter keys them (charts/boardFilterCategories.js). Other's is every type it stands for. Null
 * when the colour has no type to filter by (only trades without one).
 * @param {Flows} flows
 * @param {ProductSlot} slot
 * @returns {?{eggType: string[]}}
 */
export function productFilter(flows, slot) {
  const types = typesOf(flows.products.find(b => b.slot === slot));
  return types.length ? {eggType: types} : null;
}

/**
 * The Trades board's filter for one ribbon: its colour's egg types in that lane, and the lane's
 * seller and buyer regions, as the board's Seller Region and Buyer Region filters key them. A
 * part the trades don't have (no type, no region) is left out; null when nothing is left.
 * @param {Flows} flows
 * @param {string} from The lane's seller region.
 * @param {string} to Its buyer region.
 * @param {ProductSlot} slot
 * @returns {?{eggType?: string[], sellerRegion?: string[], buyerRegion?: string[]}}
 */
export function ribbonFilter(flows, from, to, slot) {
  const lane = flows.lanes.find(l => l.from === from && l.to === to);
  if (!lane) {
    return null;
  }
  const types = typesOf(lane.bands.find(b => b.slot === slot));
  const filter = {
    ...(types.length ? {eggType: types} : {}),
    ...(from !== '?' ? {sellerRegion: [from]} : {}),
    ...(to !== '?' ? {buyerRegion: [to]} : {}),
  };
  return Object.keys(filter).length ? filter : null;
}

// -- What the card shows ---------------------------------------------------------------------

/** @typedef {'today' | 'previous' | 'week'} FlowDay */
/** What the card draws: one day (today's, or the previous report's before today's first trade) or the past week. */
/** @typedef {'day' | 'week'} FlowSpan */

/** The board days the Week view draws: today's and the weekdays before it. */
export const WEEK_DAYS = 5;

const BOARD_DATE_FORMATS = ['MM/DD/YY', 'M/D/YY'];
const boardDate = date => moment(date || '', BOARD_DATE_FORMATS, true);

/** `count` weekdays back from a board day ("m/d/y"), oldest first, as moments. */
function weekdaysBefore(boardDay, count) {
  const day = boardDate(boardDay);
  if (!day.isValid()) {
    return [];
  }
  const days = [];
  const d = day.clone();
  while (days.length < count) {
    d.subtract(1, 'day');
    if (d.isoWeekday() <= 5) {
      days.unshift(d.clone());
    }
  }
  return days;
}

/**
 * The days the Week view fetches: the WEEK_DAYS - 1 weekdays before the board day `boardDay` (as
 * get_trades sends it, "m/d/y"), oldest first, as get_trades_history takes them ("YYYY-MM-DD").
 * None when the board day doesn't parse. A holiday is asked for like any weekday: it has no trades.
 */
export const weekBefore = boardDay => weekdaysBefore(boardDay, WEEK_DAYS - 1).map(d => d.format('YYYY-MM-DD'));

/** The Week view's days, first to last: "Sep 30 – Oct 6"; '' when the board day doesn't parse. */
export function weekRange(boardDay) {
  const [first] = weekdaysBefore(boardDay, WEEK_DAYS - 1);
  return first ? `${first.format('MMM D')} – ${boardDate(boardDay).format('MMM D')}` : '';
}

/**
 * Whose trades the card draws: today's once there are any; before the day's first trade, the
 * previous report's (`previous`: null unless it is of an earlier day); null (no card) while
 * today's board is loading or when neither has a trade.
 * @param {?FlowTrade[] | undefined} today
 * @param {?FlowTrade[] | undefined} previous
 * @returns {?{trades: FlowTrade[], day: FlowDay}}
 */
export function flowSource(today, previous) {
  if (!today) {
    return null;
  }
  if (today.length) {
    return {trades: today, day: 'today'};
  }
  return previous && previous.length ? {trades: previous, day: 'previous'} : null;
}

/** "today", what the previous report is called ("yesterday", "Friday", "the previous day"), or "the past week". */
export const dayName = (day, previousDay) =>
  (day === 'today' ? 'today' : day === 'week' ? 'the past week' : previousDay);

/** "Where today's trades went", "Where Friday's trades went", "Where the past week's trades went". */
export const flowTitle = (day, previousDay) => `Where ${dayName(day, previousDay)}'s trades went`;

const trades = n => `${n} trade${n === 1 ? '' : 's'}`;
const capitalise = s => s.charAt(0).toUpperCase() + s.slice(1);
export const regionName = code => REGION_NAMES[code] || (code === '?' ? 'an unknown region' : code);

/** A lane that stays in one known region. (Two missing regions ('?' to '?') need not be the same.) */
const withinRegion = lane => lane.from === lane.to && lane.from !== '?';

/** "Midwest to South Central", "Within Northeast", "Regions unknown". */
const lanePlace = lane => (withinRegion(lane) ? `Within ${regionName(lane.from)}`
  : lane.from === lane.to ? 'Regions unknown'
    : capitalise(`${regionName(lane.from)} to ${regionName(lane.to)}`));

/**
 * "Midwest to South Central: 4 trades: 3 PGL, 1 LIQ.", "Within Northeast: 5 trades, all PGL."
 * @param {Lane} lane
 * @returns {string}
 */
export function laneSentence(lane) {
  const types = lane.types.length === 1
    ? `, ${lane.count === 1 ? '' : 'all '}${productName(lane.types[0].product)}`
    : `: ${lane.types.map(t => `${t.count} ${productName(t.product)}`).join(', ')}`;
  return `${lanePlace(lane)}: ${trades(lane.count)}${types}.`;
}

/**
 * The card for a screen reader: whose day, how many trades between how many regions, then every
 * lane, busiest first. Before today's first trade it says so, then reads the previous day's.
 * @param {Flows} flows
 * @param {FlowDay} day
 * @param {string} previousDay
 * @returns {string}
 */
export function flowsAccessibilityLabel(flows, day, previousDay) {
  const within = flows.lanes.filter(withinRegion).reduce((n, l) => n + l.count, 0);
  let summary;
  if (flows.lanes.length === 1) {
    const lane = flows.lanes[0];
    const all = flows.total === 1 ? '' : 'all ';
    const where = withinRegion(lane) ? `${all}within ${regionName(lane.from)}`
      : lane.from === lane.to ? 'regions unknown'
        : `${all}from ${regionName(lane.from)} to ${regionName(lane.to)}`;
    summary = `${trades(flows.total)}, ${where}`;
  } else {
    const split = within === flows.total ? 'all within a region'
      : within === 0 ? 'all between regions'
        : `${within} within a region and ${flows.total - within} between regions`;
    summary = `${trades(flows.total)} among ${flows.regions.length} regions, ${split}`;
  }
  const lead = day === 'previous' ? 'No trades yet today. ' : '';
  return [`${lead}${capitalise(dayName(day, previousDay))}: ${summary}.`, ...flows.lanes.map(laneSentence)].join(' ');
}

// -- Geometry -----------------------------------------------------------------------------------

/**
 * @typedef {object} FlowSizing
 * @property {number} fontSize
 * @property {number} lineHeight A label's line: the least a row is tall, so its labels never
 *   overlap the next row's.
 * @property {number} labelWidth
 * @property {number} labelGap Between a label and its bar.
 * @property {number} barWidth
 * @property {number} rowGap
 * @property {number} targetHeight The plot height wanted; layoutFlows can come out shorter (few
 *   trades) or taller (many rows).
 * @property {number} maxK Points per trade: at most (a quiet day's ribbons stay thin), at least
 *   (a busy day's grow the plot instead: a 1-trade band keeps enough colour to tell its product).
 * @property {number} minK
 * @property {number} minBar A bar is never thinner than this: a 1-trade region stays visible.
 */

/** A chart at least this wide (on the web, the card body's content width) gets the larger chart and labels. */
export const WIDE = 600;
/** Text grows with the system text size up to this; the card's accessible label carries the rest. */
export const MAX_FONT_SCALE = 1.6;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** The label beside a region's bar: "NE 12". */
export const nodeLabel = (region, count) => `${region} ${count}`;

/**
 * Sizes for a chart `width` wide (the plot, labels included) at the system text scale. A label's
 * width is estimated from its characters (capitals and digits run about 0.62 em in the system
 * font), capped so the ribbons keep most of the width. The web has no text-size scaling and
 * passes fontScale 1.
 * @param {number} width
 * @param {Flows} flows
 * @param {number} [fontScale]
 * @returns {FlowSizing}
 */
export function flowSizing(width, flows, fontScale = 1) {
  const wide = width >= WIDE;
  const scale = clamp(fontScale || 1, 0.8, MAX_FONT_SCALE);
  const fontSize = wide ? 15 : 13;
  const labels = flows.regions.flatMap(r => [
    flows.sold[r] ? nodeLabel(r, flows.sold[r]) : '', flows.bought[r] ? nodeLabel(r, flows.bought[r]) : '',
  ]);
  const chars = Math.max(4, ...labels.map(l => l.length));
  return {
    fontSize,
    lineHeight: Math.ceil(fontSize * 1.3 * scale),
    labelWidth: clamp(Math.ceil(chars * fontSize * 0.62 * scale) + 4, 24, Math.floor(width * 0.28)),
    labelGap: wide ? 8 : 6,
    barWidth: wide ? 8 : 6,
    rowGap: wide ? 10 : 8,
    // Taller with more regions; in a wide chart also with the width, so ribbons stretched
    // across it still drop visibly from row to row (the plot no flatter than about 3:1).
    targetHeight: clamp(Math.max(flows.regions.length * (wide ? 56 : 40), wide ? Math.round(width * 0.3) : 0),
      wide ? 160 : 120, wide ? 460 : 320),
    maxK: wide ? 16 : 12,
    minK: wide ? 4 : 3,
    minBar: 3,
  };
}

/**
 * A region's bar, on the seller (left) or buyer (right) side.
 * @typedef {object} FlowNode
 * @property {string} region
 * @property {'seller' | 'buyer'} side
 * @property {number} count
 * @property {number} x
 * @property {number} y
 * @property {number} width
 * @property {number} height
 * @property {number} labelX The label's box: beside the bar, centred on it unless that would
 *   overlap a neighbour.
 * @property {number} labelY
 * @property {number} labelWidth
 * @property {string} label
 */

/**
 * One product's trades in one lane: a filled ribbon from its place in the seller's bar to its place
 * in the buyer's. A lane's bands sit in different product groups, so each is a ribbon of its own.
 * @typedef {object} FlowBand
 * @property {string} key "MW>NE:PGL"
 * @property {string} from
 * @property {string} to
 * @property {ProductSlot} slot
 * @property {number} count
 * @property {number} x0
 * @property {number} y0 The band's top and bottom at the seller's bar...
 * @property {number} y0b
 * @property {number} x1
 * @property {number} y1 ...and at the buyer's.
 * @property {number} y1b
 * @property {string} path
 * @property {string} edges Its two long edges (no ends): stroked in the surface colour, edgeWidth wide.
 */

/**
 * @typedef {object} FlowLayout
 * @property {number} width
 * @property {number} height
 * @property {number} k Points per trade.
 * @property {number} edgeWidth The bands' outlines: thinner as k gets small, so a 1-trade band
 *   keeps most of its colour.
 * @property {{region: string, y: number, height: number}[]} rows
 * @property {FlowNode[]} nodes
 * @property {FlowBand[]} bands In drawing order: the busiest bands first, so a thin one crossing
 *   them stays on top.
 */

const r2 = n => Math.round(n * 100) / 100;

/**
 * A ribbon from x0 (top y0, bottom y0b) to x1 (top y1, bottom y1b): two cubic curves, level at
 * both ends, bending halfway across, joined by straight ends.
 */
export function ribbonPath(x0, y0, y0b, x1, y1, y1b) {
  const xm = r2((x0 + x1) / 2);
  const [a, b, c, d, e, f] = [x0, y0, y0b, x1, y1, y1b].map(r2);
  return `M${a} ${b}C${xm} ${b} ${xm} ${e} ${d} ${e}L${d} ${f}C${xm} ${f} ${xm} ${c} ${a} ${c}Z`;
}

/** The same ribbon's top and bottom curves only, as open lines to stroke: its outline without ends. */
export function ribbonEdges(x0, y0, y0b, x1, y1, y1b) {
  const xm = r2((x0 + x1) / 2);
  const [a, b, c, d, e, f] = [x0, y0, y0b, x1, y1, y1b].map(r2);
  return `M${a} ${b}C${xm} ${b} ${xm} ${e} ${d} ${e}M${a} ${c}C${xm} ${c} ${xm} ${f} ${d} ${f}`;
}

/**
 * Label tops for boxes `height` tall centred on `centres` (any order), moved apart as little as
 * possible so none overlaps the next (`gap` between them), all within top..bottom when they fit.
 * Returned in the order given.
 * @param {number[]} centres
 * @param {number} height
 * @param {number} top
 * @param {number} bottom
 * @param {number} [gap]
 * @returns {number[]}
 */
export function placeLabels(centres, height, top, bottom, gap = 0) {
  const order = centres.map((c, i) => i).sort((a, b) => centres[a] - centres[b]);
  const tops = new Array(centres.length);
  // Down from the top: each below the one above.
  order.forEach((i, n) => {
    const want = clamp(centres[i] - height / 2, top, bottom - height);
    tops[i] = n === 0 ? want : Math.max(want, tops[order[n - 1]] + height + gap);
  });
  // Up from the bottom: back inside, each above the one below (the first may stay above `top`
  // only when they can't all fit).
  for (let n = order.length - 1; n >= 0; n--) {
    const limit = n === order.length - 1 ? bottom - height : tops[order[n + 1]] - height - gap;
    tops[order[n]] = Math.min(tops[order[n]], limit);
  }
  if (order.length) {
    tops[order[0]] = Math.max(tops[order[0]], top);
  }
  return tops;
}

/**
 * The chart's geometry, `width` wide: one row per region (chart order), as tall as its busier side
 * (trades × k) and at least a label's line; a ribbon per lane and product from the seller's bar to
 * the buyer's, k points per trade.
 *
 * Inside a bar, the bands group by product, in PRODUCT_SLOTS order, on both sides: a region's PGL
 * leaves (and arrives) together, then its LIQ, and so on, even though ribbons of different products
 * then cross. Within a product, bands stack in the order of the region at their other end (going
 * out, by buyer; coming in, by seller), which keeps that product's crossings to the fewest. The
 * busier side's bar is centred in its row; the other side's sits where the region's own lane runs
 * level, on average over its products, as far as the row allows (centred when there is no such
 * lane). k fills `targetHeight` when it can, within minK..maxK.
 * @param {Flows} flows
 * @param {number} width
 * @param {FlowSizing} sizing
 * @returns {FlowLayout}
 */
export function layoutFlows(flows, width, sizing) {
  const {lineHeight, labelWidth, labelGap, barWidth, rowGap, targetHeight, maxK, minK, minBar} = sizing;
  const rowIndex = new Map(flows.regions.map((r, i) => [r, i]));
  const busier = flows.regions.map(r => Math.max(flows.sold[r] || 0, flows.bought[r] || 0));
  const gaps = Math.max(0, flows.regions.length - 1) * rowGap;
  const rowHeight = (n, k) => Math.max(lineHeight, n > 0 ? Math.max(minBar, n * k) : 0);
  const total = k => busier.reduce((sum, n) => sum + rowHeight(n, k), 0) + gaps;

  // The k that fills the target: the total grows with k, so halve the interval until it's found.
  let k;
  if (total(maxK) <= targetHeight) {
    k = maxK;
  } else if (total(minK) >= targetHeight) {
    k = minK;
  } else {
    let lo = minK;
    let hi = maxK;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (total(mid) > targetHeight) {
        hi = mid;
      } else {
        lo = mid;
      }
    }
    k = lo;
  }

  const rows = [];
  let y = 0;
  flows.regions.forEach((region, i) => {
    const h = rowHeight(busier[i], k);
    rows.push({region, y, height: h});
    y += h + rowGap;
  });
  const height = flows.regions.length ? y - rowGap : 0;

  const leftX = labelWidth + labelGap;
  const rightX = width - labelWidth - labelGap - barWidth;
  const x0 = leftX + barWidth;
  const x1 = rightX;

  // Every lane's bands, busiest lane first, each lane's in PRODUCT_SLOTS order.
  const parts = flows.lanes.flatMap(lane =>
    lane.bands.map(band => ({lane, band, key: `${lane.from}>${lane.to}:${band.slot}`})));
  // Each band's place in its seller's stack and its buyer's, from the stack's top: by product,
  // then by the row at its other end.
  const outOffset = new Map();
  const inOffset = new Map();
  const stack = (of, otherEnd, offsets) => {
    let offset = 0;
    of.sort((a, b) => slotIndex(a.band.slot) - slotIndex(b.band.slot)
      || rowIndex.get(otherEnd(a)) - rowIndex.get(otherEnd(b)))
      .forEach(p => {
        offsets.set(p.key, offset);
        offset += p.band.count * k;
      });
  };
  flows.regions.forEach(region => {
    stack(parts.filter(p => p.lane.from === region), p => p.lane.to, outOffset);
    stack(parts.filter(p => p.lane.to === region), p => p.lane.from, inOffset);
  });

  // Each bar's top. The busier side's bar fills its row (or is centred in a row a label's line
  // tall). The other side's is placed so the region's own lane starts and ends at the same height
  // (its products' bands, weighted by trades, on average: they can sit at different depths in the
  // two bars), within the row; with no such lane it is centred too.
  const barTop = {};
  const barHeight = count => Math.max(minBar, count * k);
  // Where a bar's stack of bands starts: a bar at minBar is taller than its bands, which sit in
  // its middle.
  const stackInset = count => (barHeight(count) - count * k) / 2;
  flows.regions.forEach((region, i) => {
    const row = rows[i];
    const sold = flows.sold[region] || 0;
    const bought = flows.bought[region] || 0;
    const [anchor, anchorCount, other, otherCount] = sold >= bought
      ? ['seller', sold, 'buyer', bought] : ['buyer', bought, 'seller', sold];
    const anchorTop = row.y + (row.height - barHeight(anchorCount)) / 2;
    barTop[`${anchor}:${region}`] = anchorTop;
    if (!otherCount) {
      return;
    }
    const h = barHeight(otherCount);
    const own = parts.filter(p => p.lane.from === region && p.lane.to === region);
    const offset = (side, p) => (side === 'seller' ? outOffset : inOffset).get(p.key);
    const ownCount = own.reduce((n, p) => n + p.band.count, 0);
    const shift = own.reduce((sum, p) => sum + p.band.count * (offset(anchor, p) - offset(other, p)), 0)
      / (ownCount || 1);
    barTop[`${other}:${region}`] = ownCount
      ? clamp(anchorTop + stackInset(anchorCount) + shift - stackInset(otherCount), row.y, row.y + row.height - h)
      : row.y + (row.height - h) / 2;
  });

  const nodes = [];
  ['seller', 'buyer'].forEach(side => {
    const counts = side === 'seller' ? flows.sold : flows.bought;
    flows.regions.forEach(region => {
      const count = counts[region] || 0;
      if (!count) {
        return;
      }
      nodes.push({
        region, side, count,
        x: side === 'seller' ? leftX : rightX, y: barTop[`${side}:${region}`], width: barWidth,
        height: barHeight(count),
        labelX: side === 'seller' ? 0 : rightX + barWidth + labelGap, labelY: 0, labelWidth,
        label: nodeLabel(region, count),
      });
    });
  });
  // Labels centred on their bars, moved apart if two would touch (rows a line tall keep them
  // apart already; this holds for any sizing).
  ['seller', 'buyer'].forEach(side => {
    const own = nodes.filter(n => n.side === side);
    const tops = placeLabels(own.map(n => n.y + n.height / 2), lineHeight, 0, Math.max(height, lineHeight), 0);
    own.forEach((n, i) => {
      n.labelY = tops[i];
    });
  });

  // Busiest first (ties keep the lanes' order): drawn in that order, the thinner bands go on top.
  const bands = parts.map(({lane, band, key}) => {
    const y0 = barTop[`seller:${lane.from}`] + stackInset(flows.sold[lane.from]) + outOffset.get(key);
    const y1 = barTop[`buyer:${lane.to}`] + stackInset(flows.bought[lane.to]) + inOffset.get(key);
    const t = band.count * k;
    return {
      key, from: lane.from, to: lane.to, slot: band.slot, count: band.count,
      x0, y0, y0b: y0 + t, x1, y1, y1b: y1 + t,
      path: ribbonPath(x0, y0, y0 + t, x1, y1, y1 + t), edges: ribbonEdges(x0, y0, y0 + t, x1, y1, y1 + t),
    };
  }).sort((a, b) => b.count - a.count);

  // A quarter of a trade's thickness, at most 1 pt: half of it falls inside each edge.
  const edgeWidth = Math.min(1, k / 4);
  return {width, height, k, edgeWidth, rows, nodes, bands};
}
